import type { LaunchSibling } from "./launch-siblings";
import { shortLaunchLabel } from "./launch-stops";

/**
 * Launch visitor insights (migration 1631). The public menu reports which
 * model or place a visitor picked; the deliverable page shows the totals.
 * Only the model id and the place key travel, never anything about the
 * visitor, and only on a public token (never in a private preview).
 */

const MODEL_ID = /^[A-Za-z0-9]{11}$/;
const PLACE_KEY = /^(tag:[A-Za-z0-9._:-]{1,128}|reel:[0-9]{1,4})$/;

export type LaunchInsightBeacon = { modelId: string; placeKey: string };

/** The beacon body `{ m, place? }`, or null for anything malformed. */
export function parseLaunchInsightBeacon(body: string): LaunchInsightBeacon | null {
  if (!body || body.length > 1024) return null;
  let value: unknown;
  try {
    value = JSON.parse(body);
  } catch {
    return null;
  }
  if (!value || typeof value !== "object") return null;
  const { m, place } = value as { m?: unknown; place?: unknown };
  if (typeof m !== "string" || !MODEL_ID.test(m)) return null;
  if (place == null || place === "") return { modelId: m, placeKey: "" };
  if (typeof place !== "string" || !PLACE_KEY.test(place)) return null;
  return { modelId: m, placeKey: place };
}

/** Fire-and-forget count of one menu choice. Never throws. */
export function sendLaunchInsight(token: string | null, modelId: string | null, placeKey: string | null = null): void {
  if (!token || !modelId || !MODEL_ID.test(modelId)) return;
  try {
    const url = `/api/launch/${encodeURIComponent(token)}/place`;
    const body = JSON.stringify(placeKey ? { m: modelId, place: placeKey } : { m: modelId });
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      navigator.sendBeacon(url, body);
    } else {
      void fetch(url, { method: "POST", body, keepalive: true }).catch(() => {});
    }
  } catch {
    // Analytics only.
  }
}

export type LaunchInsightRow = { model_id: string; place_key: string; views: number };

export type LaunchInsightPlace = { key: string; label: string; views: number };
export type LaunchInsightModel = { modelId: string; label: string; picks: number; places: LaunchInsightPlace[] };
export type LaunchInsightReport = { total: number; models: LaunchInsightModel[] };

/**
 * Totals per model (times it was picked in the menu) and per place, most
 * used first, named from today's menu. A model no longer in the tour is
 * left out; a place no longer listed keeps a generic name.
 */
export function summarizeLaunchInsights(
  rows: readonly LaunchInsightRow[],
  siblings: readonly Pick<LaunchSibling, "providerModelId" | "label" | "stops">[],
): LaunchInsightReport {
  const byModel = new Map<string, LaunchInsightModel>();
  for (const s of siblings) {
    if (!s.providerModelId || byModel.has(s.providerModelId)) continue;
    byModel.set(s.providerModelId, { modelId: s.providerModelId, label: shortLaunchLabel(s.label).name, picks: 0, places: [] });
  }
  const tagLabels = new Map<string, string>();
  for (const s of siblings) for (const stop of s.stops ?? []) tagLabels.set(`${s.providerModelId}|tag:${stop.id}`, stop.label);

  const places = new Map<string, LaunchInsightPlace>();
  let total = 0;
  for (const row of rows) {
    const model = byModel.get(row.model_id);
    const views = Number.isFinite(row.views) && row.views > 0 ? row.views : 0;
    if (!model || views === 0) continue;
    total += views;
    if (!row.place_key) { model.picks += views; continue; }
    const id = `${row.model_id}|${row.place_key}`;
    let place = places.get(id);
    if (!place) {
      const reel = /^reel:(\d+)$/.exec(row.place_key);
      const label = tagLabels.get(id) ?? (reel ? `Highlight stop ${Number(reel[1]) + 1}` : "A place no longer in the menu");
      place = { key: row.place_key, label, views: 0 };
      places.set(id, place);
      model.places.push(place);
    }
    place.views += views;
  }
  const models = [...byModel.values()]
    .filter((m) => m.picks > 0 || m.places.length > 0)
    .map((m) => ({ ...m, places: [...m.places].sort((a, b) => b.views - a.views) }))
    .sort((a, b) => (b.picks + sum(b.places)) - (a.picks + sum(a.places)));
  return { total, models };
}

function sum(places: readonly LaunchInsightPlace[]): number {
  return places.reduce((n, p) => n + p.views, 0);
}
